import { useEffect, useState } from "react";
import { FiImage, FiUpload, FiX } from "react-icons/fi";
import { apiRequest } from "../../services/api";

const toSlug = (value) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const getImageUrl = (storageKey) => {
  if (!storageKey) return "";

  const baseUrl = (
    process.env.REACT_APP_ASSET_URL ||
    process.env.REACT_APP_API_URL ||
    ""
  ).replace(/\/$/, "");

  return storageKey.startsWith("http")
    ? storageKey
    : `${baseUrl}${storageKey.startsWith("/") ? "" : "/"}${storageKey}`;
};

export default function CategoryModal({
  open,
  onClose,
  onSave,
  category = null,
  accessToken,
}) {
  const [form, setForm] = useState({
    name: "",
    code: "",
    description: "",
    status: "Active",
    imageKey: "",
  });

  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  useEffect(() => {
    if (!open) return;

    setError("");
    setForm({
      name: category?.name || "",
      code: category?.code || "",
      description: category?.description || "",
      status: category?.isActive === false ? "Inactive" : "Active",
      imageKey: category?.imageKey || "",
    });
  }, [open, category]);

  if (!open) return null;

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    try {
      setIsUploadingImage(true);
      setError("");

      const formData = new FormData();
      formData.append("file", file);

      const uploaded = await apiRequest("/uploads/categories", {
        method: "POST",
        body: formData,
        accessToken,
      });

      setForm((current) => ({
        ...current,
        imageKey: uploaded.storageKey,
      }));
    } catch (requestError) {
      setError(requestError.message || "Unable to upload category image.");
    } finally {
      setIsUploadingImage(false);
      event.target.value = "";
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.name.trim() || !form.code.trim()) {
      setError("Category name and code are required.");
      return;
    }

    try {
      setIsSaving(true);
      setError("");

      await onSave({
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        slug: toSlug(form.name),
        description: form.description.trim() || undefined,
        imageKey: form.imageKey || undefined,
        isActive: form.status === "Active",
      });

      onClose();
    } catch (requestError) {
      const message = requestError?.details?.message || requestError.message;

      setError(
        Array.isArray(message)
          ? message.join(", ")
          : message || "Unable to save category.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-8 py-6">
          <div>
            <h2 className="text-2xl font-bold">
              {category ? "Edit Category" : "Add Category"}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Create and manage product categories.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={isSaving || isUploadingImage}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
          >
            <FiX size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6 p-8">
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div>
              <label className="mb-2 block text-sm font-medium">
                Category Name *
              </label>
              <input
                required
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">
                Category Code *
              </label>
              <input
                required
                value={form.code}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    code: event.target.value.toUpperCase(),
                  }))
                }
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">
                Description
              </label>
              <textarea
                rows="4"
                value={form.description}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
                className="w-full resize-none rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-primary-700"
              />
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="text-sm font-medium">Category Image</label>

                {form.imageKey && (
                  <button
                    type="button"
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        imageKey: "",
                      }))
                    }
                    className="text-xs font-medium text-red-600 hover:underline"
                  >
                    Remove image
                  </button>
                )}
              </div>

              <label className="flex h-40 cursor-pointer flex-col items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 hover:border-primary-700">
                {form.imageKey ? (
                  <img
                    src={getImageUrl(form.imageKey)}
                    alt="Category preview"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <>
                    <FiImage size={26} className="mb-2 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      {isUploadingImage
                        ? "Uploading image…"
                        : "Upload optional category image"}
                    </span>
                  </>
                )}

                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/webp"
                  disabled={isUploadingImage}
                  onChange={uploadImage}
                />
              </label>

              <p className="mt-2 text-xs text-gray-400">
                JPG, PNG, or WEBP · Maximum 5 MB.
              </p>
            </div>

            <div>
              <label className="mb-3 block text-sm font-medium">Status</label>

              <div className="flex gap-3">
                {["Active", "Inactive"].map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        status,
                      }))
                    }
                    className={`rounded-xl border px-5 py-2.5 font-medium ${
                      form.status === status
                        ? "border-primary-700 bg-primary-700 text-white"
                        : "border-gray-200"
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-100 px-8 py-5">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving || isUploadingImage}
              className="h-11 rounded-xl border border-gray-200 px-6 font-medium"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSaving || isUploadingImage}
              className="h-11 rounded-xl bg-primary-700 px-6 font-medium text-white disabled:opacity-60"
            >
              {isSaving ? "Saving…" : "Save Category"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}